import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, KeyRound, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      // No email provider configured → build the link from THIS public origin so
      // it is always reachable (the backend Origin header can be an internal host).
      if (res.data?.reset_token) {
        setResetLink(`${window.location.origin}/reset-password?token=${res.data.reset_token}`);
      } else if (res.data?.reset_link) {
        setResetLink(res.data.reset_link);
      }
      toast.success("If an account exists for that email, a reset link has been generated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start password reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <Card className="w-full max-w-md card-official">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-royal-purple/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-royal-purple" />
          </div>
          <CardTitle className="heading-card text-royal-blue">Forgot Password</CardTitle>
          <CardDescription>
            Works for Nationals, Subscribers and Admins. Enter your account email to get a reset link.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="form-label">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input pl-10"
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                    style={{ fontSize: "16px" }}
                    autoComplete="email"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-gold" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? "Sending…" : "Send Reset Link"}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4" data-testid="forgot-sent-state">
              <p className="text-sm text-slate-600">
                If an account exists for <strong>{email}</strong>, a password reset link has been generated.
              </p>
              {resetLink && (
                <div className="bg-kingdom-gold/10 border border-kingdom-gold/30 rounded p-3 text-left">
                  <p className="text-xs text-slate-700 mb-2">
                    Email delivery isn't enabled yet — use this secure reset link (valid for 1 hour):
                  </p>
                  <a
                    href={resetLink}
                    className="text-sm text-royal-blue underline break-all"
                    data-testid="forgot-reset-link"
                  >
                    {resetLink}
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 pt-6 border-t text-center">
            <Link to="/subscribers/login" className="inline-flex items-center gap-2 text-sm text-royal-blue hover:underline" data-testid="forgot-back-login">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
